import { AdminMessage } from "@components/admin/admin-helpers"
import { Slider } from "@components/atoms/Slider"
import { FilmTile } from "@components/film/FilmTile"
import { useProgramData } from "@features/program/useProgramData"
import { getLastViewedFilms } from "@features/tracking/film-history"
import { FilmTileFragment } from "@generated-types"
import { tv } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { useEffect, useState } from "react"

export const meta: BlockMeta = {
  title: "Recently Viewed Films",
  description:
    "Shows the films that the user has recently viewed, ranked by the number and type of engagements. Will be hidden if the user has interacted with less than 3 films.",
  category: "media",
  tags: ["#subpage", "#homepage", "#featured"],
  icon: "editor-video",
  keywords: ["film grid slider"],
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("content/recently-viewed", (props) => {
  const program = useProgramData()
  const [films, setFilms] = useState<FilmTileFragment[]>([])
  const styles = createStyles({
    perRow: films?.length === 3 ? 3 : films?.length === 4 ? 4 : "auto",
  })
  const [heading] = useInlineEditableValue<string>("heading")

  useEffect(() => {
    const history = getLastViewedFilms()
    const films = history
      .map((item) => program.data?.currentProgram?.films?.find((film) => film?.uri === item.uri))
      .filter((film) => !!film)
    if (films.length >= 3) {
      setFilms(films)
    } else {
      setFilms([])
    }
  }, [program.data])

  if (!films.length && !env.admin) {
    return null
  }

  return (
    <div className={styles.wrapper()} data-mixpanel-block="Recently Viewed Films List" data-mixpanel-heading={heading}>
      <div className="w-grid mx-auto flex justify-between items-center mb-3">
        <div className="type-title-m w-[20em] md:w-auto text-pretty flex">
          <EditableText as="h2" store="heading" defaultValue="Jump back in" className="" plainText />
        </div>
      </div>
      {films.length === 0 && env.admin ? (
        <div className="w-grid mx-auto mt-5">
          <AdminMessage>Recently viewed films will appear here</AdminMessage>
        </div>
      ) : (
        <Slider arrows gridInsert scrollerClass="flex gap-grid-gutter px-grid-margin-full">
          {films?.map((item, i) => (
            <div key={i} className={styles.item()}>
              <FilmTile film={item!} />
            </div>
          ))}
        </Slider>
      )}
    </div>
  )
})

const createStyles = tv({
  slots: {
    wrapper: "py-4",
    heading: "type-title-m text-pretty mb-3",
    item: "w-cols-8 sm:w-cols-7 flex-none py-[10px]",
  },
  variants: {
    perRow: {
      3: {
        item: "md:w-cols-4",
      },
      4: {
        item: "md:w-cols-3",
      },
      auto: {
        item: "md:w-cols-3 xl:w-cols-3",
      },
    },
  },
})
